import {
  configureStore,
  type Reducer,
  type UnknownAction,
} from '@reduxjs/toolkit';
import { persistReducer, persistStore } from 'redux-persist';
import storage from 'redux-persist/es/storage';
import type { PersistedState } from 'redux-persist/es/types';

import { createRootReducer, type RootState } from './rootReducer';
import type { AppThunkExtra } from './types';

/** Any RTK slice (or `{ reducerPath, reducer }` pair) a feature module contributes. */
export type SliceLike = { reducerPath: string; reducer: Reducer };

export const RESET_STATE = 'RESET_STATE';

export interface AppStoreOptions {
  /** Prefixes the persist key (`<appId>.root`) unless `persist.key` is given. */
  appId: string;
  /** Feature slices, injected before the store exists so every key is present at boot. */
  slices: SliceLike[];
  /** Extra slice keys to persist on top of `auth` and `organization`. */
  persistedSlices?: string[];
  persist?: { key?: string; version?: number };
  /** Thunk `extraArgument` (auth adapter, app metadata). */
  extra: AppThunkExtra;
}

/**
 * Builds the Redux store for one dashboard: core slices + the feature slices
 * of its modules, redux-persist for the session, and a `RESET_STATE` action
 * that wipes everything on logout / 401.
 */
export function createAppStore(options: AppStoreOptions) {
  const root = createRootReducer();
  for (const slice of options.slices) {
    root.inject(slice);
  }

  const withReset: Reducer<RootState, UnknownAction> = (state, action) =>
    action.type === RESET_STATE ? root(undefined, action) : root(state, action);

  const version = options.persist?.version ?? 3;
  const persistedReducer = persistReducer<RootState>(
    {
      key: options.persist?.key ?? `${options.appId}.root`,
      storage,
      // Bump on any persisted-slice shape change so stale snapshots get dropped
      // instead of clashing with selectors that expect the new shape.
      version,
      // Everything outside the whitelist is session data, re-fetched on mount.
      whitelist: ['auth', 'organization', ...(options.persistedSlices ?? [])],
      migrate: (state: PersistedState | undefined) =>
        Promise.resolve(state?._persist?.version === version ? state : undefined),
    },
    withReset,
  );

  const store = configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: false,
        thunk: { extraArgument: options.extra },
      }),
    devTools: import.meta.env.MODE !== 'production',
  });

  const persistor = persistStore(store);

  const resetState = () => {
    store.dispatch({ type: RESET_STATE });
    void persistor.purge();
  };

  return { store, persistor, resetState };
}

export type AppStore = ReturnType<typeof createAppStore>['store'];
